import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { api, API } from "@/lib/api";
import { IMPACT_METRICS, metricDisplay, monthLabel } from "@/lib/impact";
import { Quote, FileDown, ShieldCheck } from "lucide-react";
import dayjs from "dayjs";

// Phase 17-D item 7: public council/borough impact dashboard.
// Aggregates only; anything with n < 5 arrives suppressed from the API and
// renders as "<5" — no client-side maths on individuals.
export default function ImpactArea() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.publicImpact(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="impact-area-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl text-forest">No impact page for that area yet</h1>
        <p className="text-neutral-600 mt-3">Areas are added as CivicHearth reaches them.</p>
        <Link to="/partners" className="inline-block mt-6 text-forest font-medium underline">Talk to us about your borough</Link>
      </div>
    );
  }
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  const maxHours = Math.max(1, ...data.trend.filter((t) => !t.suppressed).map((t) => t.hours || 0));

  return (
    <div className="bg-cream min-h-screen" data-testid="impact-area-page">
      <Seo
        title={`${data.name} — community impact`}
        description={`Volunteer hours, help requests fulfilled and neighbourhood organising in ${data.name} — live, aggregate-only figures from CivicHearth.`}
        image={`${API}/og/place/${data.slug}`}
      />
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Community impact</div>
            <h1 className="font-display text-4xl sm:text-5xl text-forest mt-2 tracking-tight">{data.name}</h1>
            <p className="text-neutral-600 mt-3 max-w-xl">
              Live figures from neighbours giving time in {data.name} — generated {dayjs(data.generated_at).format("D MMM YYYY")}.
            </p>
          </div>
          <Link to={`/impact/${data.slug}/pack`}>
            <Button data-testid="impact-pack-btn" className="rounded-full bg-forest hover:bg-forest-hover h-11 px-6">
              <FileDown className="w-4 h-4 mr-2" /> Download council pack
            </Button>
          </Link>
        </div>

        {/* Headline metrics */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {IMPACT_METRICS.map((m) => {
            const v = data.metrics[m.key];
            return (
              <div key={m.key} data-testid={`impact-metric-${m.key}`} className="bg-white border border-neutral-200 rounded-2xl p-6">
                <m.icon className="w-5 h-5 text-terracotta" />
                <div className={`font-display mt-3 ${v?.suppressed ? "text-2xl text-forest/50" : "text-4xl text-forest"}`}>
                  {metricDisplay(v)}
                </div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-forest/60 font-bold mt-1">{m.label}</div>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-neutral-500 mt-3 inline-flex items-center gap-1.5" data-testid="impact-suppression-note">
          <ShieldCheck className="w-3.5 h-3.5 text-forest/50" />
          Privacy by default: any figure drawn from fewer than {data.suppress_below} people shows as “&lt;5”.
        </p>

        {/* Top causes */}
        <div className="mt-12" data-testid="impact-causes">
          <h2 className="text-base md:text-lg font-semibold text-forest">Top causes</h2>
          {data.top_causes.length === 0 ? (
            <p className="text-sm text-neutral-500 mt-2">Not enough activity to rank causes yet — check back as {data.name} grows.</p>
          ) : (
            <div className="flex flex-wrap gap-2 mt-3">
              {data.top_causes.map((c) => (
                <span key={c.cause} className="inline-flex items-center gap-1.5 bg-white border border-forest/15 rounded-full px-4 py-1.5 text-sm text-forest">
                  {c.cause} <span className="text-xs text-neutral-400">×{c.count}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* 12-month trend */}
        <div className="mt-12" data-testid="impact-trend">
          <h2 className="text-base md:text-lg font-semibold text-forest">Approved hours — last 12 months</h2>
          {data.trend.length === 0 ? (
            <p className="text-sm text-neutral-500 mt-2">No approved hours recorded in {data.name} in the last 12 months.</p>
          ) : (
            <div className="mt-4 bg-white border border-neutral-200 rounded-2xl p-6">
              <div className="flex items-end gap-3 h-36">
                {data.trend.map((t) => (
                  <div key={t.month} className="flex-1 flex flex-col items-center justify-end h-full" title={t.suppressed ? "Hidden — fewer than 5 volunteers" : `${t.hours}h`}>
                    {t.suppressed ? (
                      <div className="w-full h-8 rounded-t-md border-2 border-dashed border-neutral-300" />
                    ) : (
                      <div className="w-full bg-terracotta/70 rounded-t-md" style={{ height: `${Math.max(6, ((t.hours || 0) / maxHours) * 100)}%` }} />
                    )}
                    <div className="text-[10px] text-neutral-500 mt-2 whitespace-nowrap">{monthLabel(t.month)}</div>
                  </div>
                ))}
              </div>
              {data.trend.some((t) => t.suppressed) && (
                <p className="text-[11px] text-neutral-400 mt-3">Dashed months are hidden — fewer than {data.suppress_below} volunteers contributed.</p>
              )}
            </div>
          )}
        </div>

        {/* Featured stories */}
        {data.stories?.length > 0 && (
          <div className="mt-12" data-testid="impact-stories">
            <h2 className="text-base md:text-lg font-semibold text-forest">From neighbours in {data.name}</h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.stories.map((s) => (
                <div key={s.id} className="bg-white border border-neutral-200 rounded-2xl p-5">
                  <Quote className="w-4 h-4 text-terracotta" />
                  <p className="text-sm text-neutral-700 mt-2 line-clamp-4">{s.text}</p>
                  <div className="text-xs text-neutral-500 mt-3">— {s.user_name}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Partner band */}
        <div className="mt-14 bg-forest rounded-2xl p-8 sm:p-10 text-white flex items-center justify-between flex-wrap gap-6">
          <div>
            <div className="font-display text-2xl">Working on {data.name}'s communities?</div>
            <p className="text-white/70 text-sm mt-1.5">Councils, housing associations and funders — we'd love to share the full picture.</p>
          </div>
          <Link to="/partners#councils">
            <Button data-testid="impact-partners-cta" className="rounded-full bg-terracotta hover:bg-terracotta-hover px-7 h-12">Talk to us</Button>
          </Link>
        </div>
        {/* 2026-07: professionals line — packs get printed, keep the URL visible */}
        <p className="mt-4 text-sm text-neutral-600" data-testid="impact-professionals-line">
          Working with professionals: link workers and GPs can refer someone in minutes —{" "}
          <Link to="/for-professionals" className="text-forest font-medium underline underline-offset-2">
            civichearth.com/for-professionals
          </Link>
        </p>
      </div>
    </div>
  );
}
